import FormIcon from "src/assets/icons/SsqTitleIcon/form.svg?react";
import PotIcon from "src/assets/icons/SsqTitleIcon/pot.svg?react";
import BoxIcon from "src/assets/icons/SsqTitleIcon/box.svg?react";
import CutleryIcon from "src/assets/icons/SsqTitleIcon/cutlery.svg?react";
import MarketIcon from "src/assets/icons/SsqTitleIcon/market.svg?react";
// import ProductionIcon from "src/assets/icons/SsqTitleIcon/production.svg?react";
import InfoIcon from "src/assets/icons/SsqTitleIcon/info.svg?react";

import { Page1 } from "./Pages/Page1";
import { Page2 } from "./Pages/Page2";
import { Page3 } from "./Pages/Page3";
// import { Page4 } from "./Pages/Page4";
import { Page5 } from "./Pages/Page5";
import { Page6 } from "./Pages/Page6";
import { Page7 } from "./Pages/Page7";
import {
    ConfirmDialog,
    FoodsBadge,
    FormBaseContextProvider,
    FormTopBar,
    useFormBaseContext,
    type FoodsPage,
} from "@foodpilot/foods";

import { useNavigate, useLocation } from "react-router";
import { Box, Stack, Typography, useTheme } from "@mui/material";
import { useTranslation } from "react-i18next";
import { useUpsertProduct } from "src/api/client-api/products/useUpsertProduct";
import { useUploadProductPhoto } from "src/api/client-api/products/useProductPhotos";
import { Product } from "src/api/client-api/products/useGetOneProduct";
import { useState, useEffect, useRef } from "react";
import { ProductService } from "../ProductService";
import { ProductFormType } from "./ProductForm.types";
import { URLHandler } from "src/services/URLHandler";
import { ProductReference } from "src/api/sure-api/productReference/useGetOneProductReference";
import { useQueryClient } from "react-query";
import { productGridKey } from "src/api/client-api/products/useGetAllproduct";
import { useApplicationParameters } from "src/api/client-api/appParams/useApplicationParameters";
import {
    ConfirmationModalData,
    DataDeletionWarningDialog,
} from "src/components/Dialog/Templates/DataDeletionWarningDialog";
import { SsqPostChangedWarnings } from "src/components/Dialog/Templates/AllWarnings/SsqPostChangedWarnings";
import { AssociatedStructureWarning } from "src/components/Dialog/Templates/AllWarnings/AssociatedStructureWarning";

type ProductFormProps = {
    existingProduct: Product | undefined;
    companyIds: {
        id: number;
        "@id": IRI;
        name: string;
    };
    productReference: ProductReference | undefined;
    startingPageId: string;
    isIntermediate?: boolean;
};
export const ProductForm = (props: ProductFormProps) => {
    const { existingProduct, productReference, startingPageId, companyIds, isIntermediate = false } = props;
    const { t } = useTranslation();
    const navigate = useNavigate();
    const location = useLocation();
    const createProduct = useUpsertProduct();
    const uploadProductPhoto = useUploadProductPhoto();
    const queryClient = useQueryClient();
    const theme = useTheme();
    const { isFeatureActivated } = useApplicationParameters();
    const isProductConsumptionEnabled = isFeatureActivated("FS-product-consumption");
    const isProductDistributionEnabled = isFeatureActivated("FS-product-distribution");
    const isPackagingEnabled = isFeatureActivated("FS-entity-packagings");
    const isIngredientsEnabled = isFeatureActivated("FS-entity-ingredients");

    const formData = ProductService.getProductToCreate({
        existingProduct,
        productReference,
    });
    // Set isIntermediate based on prop if creating a new product.
    // Default `ownedBy` to the active company on creation so the form state matches
    // what `productFormToProductWrite` will send anyway, and so `isOwner` evaluates
    // to true from the start (drives `AssociatedStructuresField` visibility and hides
    // the redundant `OwnedByField`).
    const initialProduct = {
        ...formData,
        isIntermediate: formData.id !== undefined ? formData.isIntermediate : isIntermediate,
        ownedBy:
            formData.ownedBy ??
            (existingProduct === undefined ?
                { "@id": companyIds["@id"], id: companyIds.id, name: companyIds.name }
            :   null),
    };
    const [product, setProduct] = useState<ProductFormType>(initialProduct);

    // Extraire l'URL de retour depuis les paramètres
    const searchParams = new URLSearchParams(location.search);
    const returnUrl = searchParams.get("returnUrl");
    const productListUrl =
        returnUrl ? decodeURIComponent(returnUrl)
        : isIntermediate ? URLHandler.intermediateProduct.list(companyIds.id)
        : URLHandler.product.list(companyIds.id);
    const [isConfirmationModalOpen, setConfirmationModalOpen] = useState<ConfirmationModalData>({
        isOpen: false,
        errors: null,
    });

    const productCompanies = product.shares.map((pC) => pC.organization);
    const initEntities = useRef(productCompanies);
    const newItemsIds = productCompanies.map((entity) => {
        return entity.id;
    });
    const deletedEntities = initEntities.current
        .filter((i) => {
            const stillHasIt = newItemsIds.includes(i.id);
            return stillHasIt === false;
        })
        .map((i) => {
            return i.name;
        })
        .join(",");

    // Only update form state on initial load, not on subsequent changes
    // Tags are now managed in local form state only
    useEffect(() => {
        if (existingProduct && product.id === undefined) {
            // Only update on initial load when we don't have a product ID yet

            const updatedFormData = ProductService.getProductToCreate({
                existingProduct,
                productReference,
            });
            setProduct(updatedFormData);
        }
    }, [existingProduct, productReference, product.id]);

    const allPages: FoodsPage[] = [
        {
            // title: t("common.general_information", "General information"),
            icon: <InfoIcon />,
            content: <Page1 product={product} setProduct={setProduct} companyIds={companyIds} />,
        },
        ...(isIngredientsEnabled ?
            [
                {
                    title: t("product.recipe.title", "Recipe"),
                    icon: <PotIcon />,
                    content: <Page2 product={product} setProduct={setProduct} />,
                },
            ]
        :   []),
        ...(isPackagingEnabled ?
            [
                {
                    title: t("product.packagings.title", "Packagings"),
                    icon: <BoxIcon />,
                    content: <Page3 product={product} setProduct={setProduct} />,
                },
            ]
        :   []),
        // {
        //     title: t("product.production.title", "Production"),
        //     icon: <ProductionIcon />,
        //     content: <Page4 product={product} setProduct={setProduct} />,
        // },
        ...(isProductDistributionEnabled ?
            [
                {
                    title: t("product.distribution.title", "Distribution"),
                    icon: <MarketIcon />,
                    content: <Page5 product={product} setProduct={setProduct} />,
                },
            ]
        :   []),
        ...(isProductConsumptionEnabled ?
            [
                {
                    title: t("product.consumption.title", "Consumption"),
                    icon: <CutleryIcon />,
                    content: <Page6 product={product} setProduct={setProduct} />,
                },
            ]
        :   []),
        {
            title: t("product.form.summary_title", "Summary of data entered"),
            icon: <FormIcon />,
            content: <Page7 product={product} />,
        },
    ];
    const isNew = existingProduct === undefined;

    // pageId can be an invalid string.
    // If the string is invalid, result is NaN
    // It is not shown in the types.
    const pageId = Number.parseInt(startingPageId);

    if (isNaN(pageId)) {
        return (
            <Typography variant="big-bold">
                {t("product.form.page_not_found", "The page you're trying access does not exist")}
            </Typography>
        );
    }

    const handleProductUpdate = async () => {
        try {
            const options = {
                product: ProductService.productFormToProductWrite(product, undefined, {
                    isProductDistributionEnabled,
                    isProductConsumptionEnabled,
                }),
                organizationId: companyIds.id,
            };

            // Prefer mutateAsync if available
            let response;
            if ((createProduct as any).mutateAsync) {
                response = await (createProduct as any).mutateAsync(options);
            } else {
                response = await new Promise<any>((resolve, reject) =>
                    createProduct.mutate(options, {
                        onSuccess: (res) => resolve(res),
                        onError: reject,
                    }),
                );
            }

            // Invalidate product queries to refresh the list with updated data
            queryClient.invalidateQueries([productGridKey]);

            // Redirect to scores page instead of list
            const productId = response?.data?.id || product.id;
            if (productId) {
                const pendingProductPhotos = product.pendingProductPhotos ?? [];
                await Promise.all(
                    pendingProductPhotos.map((file, index) =>
                        uploadProductPhoto.mutateAsync({
                            productId,
                            file,
                            isPrimary: (product.photos ?? []).length === 0 && index === 0,
                        }),
                    ),
                );

                const redirectUrl =
                    product.isIntermediate ?
                        URLHandler.intermediateProduct.scores(companyIds.id, productId)
                    :   URLHandler.product.scores(companyIds.id, productId);
                navigate(redirectUrl);
            } else {
                const fallbackUrl =
                    product.isIntermediate ? URLHandler.intermediateProduct.list(companyIds.id) : productListUrl;
                navigate(fallbackUrl);
            }
        } catch (e) {
            // Errors will be shown by global handlers; keep here to stop silent failures

            console.error(e);
        }
    };
    const subtitle = product.clientId ? `${product.title || ""} - ${product.clientId}` : `${product.title || ""}`;

    return (
        <Box component="div" sx={{ marginLeft: "auto", marginRight: "auto", padding: "44px 52px " }}>
            <DataDeletionWarningDialog
                confirmationData={isConfirmationModalOpen}
                setConfirmationData={setConfirmationModalOpen}
                onConfirm={() => {
                    handleProductUpdate();
                }}
            />

            <FormBaseContextProvider
                showSync={false}
                startingPage={pageId}
                onClose={() => navigate(productListUrl)}
                textOptions={{
                    title: (
                        <Stack flexDirection={"row"} alignItems={"center"} gap={theme.spacing(2)}>
                            <FoodsBadge
                                size={4}
                                icon={"products"}
                                boxProps={{
                                    sx: {
                                        minWidth: "48px",
                                        minHeight: "48px",
                                        borderRadius: "25px",
                                        backgroundColor: theme.custom.grey[400],
                                    },
                                }}
                            />
                            <Stack textOverflow={"clip"}>
                                <Typography variant="h3">
                                    {isNew ?
                                        t("product.form.creation_title", "New product creation")
                                    :   t("product.form.modification_title", "Modify the product")}
                                </Typography>
                            </Stack>
                        </Stack>
                    ),
                    subtitle: subtitle,
                    nextPage: t("common.actions.next_step", "Next step"),
                    previousPage: t("common.actions.previous", "Previous"),
                    finalPage: t("common.actions.save", "Save"),
                    warningText: t("common.warning", "Warning!"),
                }}
                pages={allPages}
                onValidate={() => {
                    const errors = [];
                    const hasSsqChanged =
                        existingProduct?.ssqId !== undefined && existingProduct.ssqId !== product.ssq_id;
                    if (hasSsqChanged) {
                        errors.push(
                            <SsqPostChangedWarnings
                                sharedOrganizationNames={existingProduct?.shares?.map(
                                    (share) => share.organization.name,
                                )}
                            />,
                        );
                        // setConfirmationModalOpen(true);
                    }
                    if (deletedEntities.length > 0) {
                        errors.push(<AssociatedStructureWarning deletedEntities={deletedEntities} />);
                    }

                    if (errors.length > 0) {
                        setConfirmationModalOpen({
                            isOpen: true,
                            errors,
                        });
                        return;
                    }

                    handleProductUpdate();
                }}
                confirmExit={true}
                confirmOptions={{
                    titleText:
                        isNew ?
                            t(
                                "product.form.quit_dialog.creation_confirm",
                                "Do you really wish to exit product creation?",
                            )
                        :   t(
                                "product.form.quit_dialog.modification_confirm",
                                "Do you really wish to exit product modification?",
                            ),
                    confirmText:
                        isNew ?
                            t("product.form.quit_dialog.delete_and_quit", "Delete and exit")
                        :   t("product.form.quit_dialog.dont_save_and_quit", "Don't save and leave"),
                    cancelText:
                        isNew ?
                            t("product.form.quit_dialog.dont_quit_creation", "Don't leave the creation")
                        :   t("product.form.quit_dialog.dont_quit_modification", "Don't leave the modification"),
                    onConfirm: () => navigate(productListUrl),
                    onCancel: () => {
                        // Do nothing;
                    },
                }}
            >
                <ConfirmDialog />
                <FormTopBar />
                <CurrentPage />
            </FormBaseContextProvider>
        </Box>
    );
};

const CurrentPage = () => {
    const { selectedPage } = useFormBaseContext();
    return <Stack paddingTop={0}>{selectedPage ? selectedPage.content : null}</Stack>;
};
